import { useMemo, useState } from "react";
import { BlockLink } from "@/components/DrilldownLink";
import HeroStatusChip from "@/components/HeroStatusChip";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v) => (typeof v === "number" ? `${v}%` : "0%");

const DONUT_META = {
  "CTET Qualified": { variant: "", subtitle: "Highest improvement priority" },
  "NISHTHA Completed": { variant: "", subtitle: "Training gap remains high" },
  "Aadhaar Verified": { variant: "purple", subtitle: "Strong verification base" },
  "Female Representation": { variant: "pink", subtitle: "Positive representation signal" },
};

const shortenQualification = (name) => {
  if (!name) return "";
  const str = String(name);
  if (str.includes("B.Ed") && str.includes("equivalent")) return "B.Ed or Equivalent";
  if (str.includes("M.Ed") && str.includes("equivalent")) return "M.Ed or Equivalent";
  if (str.includes("Diploma") && str.includes("basic teacher")) return "Basic Teacher Diploma";
  if (str.includes("D.El.Ed")) return "D.El.Ed";
  if (str.includes("None")) return "Non/Untrained";
  if (str.includes("special education")) return "Special Education";
  if (str.includes("Others")) return "Others";
  if (str.includes("Post graduate")) return "Post Graduate";
  if (str.includes("Graduate") && !str.includes("Post")) return "Graduate";
  if (str.includes("Higher Secondary")) return "Higher Secondary";
  if (str.includes("Secondary") && !str.includes("Higher")) return "Secondary";
  if (str.includes("M.Phil")) return "M.Phil";
  if (str.includes("Ph.D")) return "Ph.D";
  if (str.includes("Below secondary")) return "Below Secondary";
  if (str.includes(" - ")) return str.split(" - ")[1]?.substring(0, 30) || str.substring(0, 30);
  return str.length > 30 ? `${str.slice(0, 30)}…` : str;
};

const profBarClass = (value) => {
  if (value >= 30) return "green";
  if (value >= 10) return "";
  return "orange";
};

const acadBarClass = (value) => {
  if (value >= 40) return "purple";
  if (value >= 20) return "purple";
  return "orange";
};

const blockQualityIndex = (block, aadhaarPct) =>
  Math.round(((block.ctet_pct ?? 0) * 0.4 + (block.nishtha_pct ?? 0) * 0.3 + aadhaarPct * 0.3) * 10) / 10;

const priorityTag = (index) => {
  if (index < 40) return { label: "High", className: "red" };
  if (index < 48) return { label: "Medium", className: "amber" };
  return { label: "Good", className: "green" };
};

const MetricDonut = ({ value, display, label, subtitle, variant = "" }) => (
  <div className="ed-teacher-metric-donut">
    <div className={`ed-teacher-donut ${variant}`} style={{ "--pct": `${Math.min(Math.max(value, 0), 100)}%` }}>
      <strong>{display ?? pct(value)}</strong>
    </div>
    <b>{label}</b>
    <span>{subtitle}</span>
  </div>
);

const KpiCard = ({ label, value, hint, iconVariant, onClick, children }) => (
  <article
    className={`ed-teacher-kpi-card${onClick ? " clickable" : ""}`}
    onClick={onClick}
    onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    <div className="ed-teacher-kpi-top">
      <small>{label}</small>
      <span className={`ed-teacher-mini-icon ${iconVariant || ""}`}>{children}</span>
    </div>
    <strong>{value}</strong>
    <span>{hint}</span>
  </article>
);

const BarList = ({ items, barClassFn }) => (
  <div className="ed-teacher-bar-list">
    {items.map((item) => (
      <div className="ed-teacher-bar-row" key={item.label}>
        <div className="ed-teacher-bar-label">{item.label}</div>
        <div className="ed-teacher-bar-track">
          <div className={`ed-teacher-bar ${barClassFn(item.percentage)}`} style={{ width: `${Math.max(item.percentage, 2)}%` }} />
        </div>
        <div className="ed-teacher-bar-value">{item.percentage.toFixed(1)}%</div>
      </div>
    ))}
  </div>
);

const ExecutiveTeacherStaffingTab = ({ teacherData, scopeLabel, onSchoolCountClick }) => {
  const [priorityOnly, setPriorityOnly] = useState(false);

  const summary = teacherData?.summary ?? {};
  const compliance = teacherData?.compliance_metrics ?? {};
  const demographic = teacherData?.demographic_metrics ?? {};
  const risk = teacherData?.risk_metrics ?? {};
  const distribution = teacherData?.school_teacher_distribution ?? {};
  const qualityBreakdown = Array.isArray(teacherData?.quality_breakdown) ? teacherData.quality_breakdown : [];
  const blocks = teacherData?.block_performance ?? [];
  const qualMetrics = teacherData?.qualification_metrics ?? {};

  const qualityIndex = summary.teacher_quality_index ?? 0;
  const totalTeachers = summary.total_teachers ?? 0;
  const ctetPct = compliance.ctet_pct ?? 0;
  const nishthaPct = compliance.nishtha_pct ?? 0;
  const aadhaarPct = compliance.aadhaar_verified_pct ?? 0;
  const femalePct = demographic.female_pct ?? 0;
  const zeroSchools = distribution.zero_teacher_schools ?? 0;
  const oneSchools = distribution.one_teacher_schools ?? 0;
  const twoSchools = distribution.two_teacher_schools ?? 0;

  const weakestQualitySignal = useMemo(() => {
    const signals = [
      { name: "CTET qualification", value: ctetPct },
      { name: "NISHTHA completion", value: nishthaPct },
      { name: "Aadhaar verification", value: aadhaarPct },
      { name: "Female representation", value: femalePct },
    ];
    return [...signals].sort((a, b) => a.value - b.value)[0];
  }, [ctetPct, nishthaPct, aadhaarPct, femalePct]);

  const profBars = useMemo(
    () =>
      (qualMetrics.professional_qualification_distribution || [])
        .slice(0, 8)
        .map((item) => ({
          label: shortenQualification(item.qualification),
          percentage: item.percentage ?? 0,
        })),
    [qualMetrics],
  );

  const acadBars = useMemo(
    () =>
      (qualMetrics.academic_qualification_distribution || [])
        .slice(0, 8)
        .map((item) => ({
          label: shortenQualification(item.qualification),
          percentage: item.percentage ?? 0,
        })),
    [qualMetrics],
  );

  const tableRows = useMemo(() => {
    const rows = blocks.map((block) => {
      const idx = block.quality_index ?? blockQualityIndex(block, aadhaarPct);
      const tag = priorityTag(idx);
      return { ...block, quality_index: idx, priority: tag };
    });
    const sorted = rows.sort((a, b) => a.quality_index - b.quality_index);
    if (priorityOnly) {
      return sorted.filter((r) => r.priority.className === "red");
    }
    return sorted;
  }, [blocks, aadhaarPct, priorityOnly]);

  const heroNarrative =
    qualityIndex < 45 && ctetPct < 15
      ? "Teacher availability is adequate at aggregate level, but quality indicators show urgent gaps in CTET qualification, NISHTHA completion and single-teacher school coverage."
      : qualityIndex < 60
        ? "Teacher workforce is present district-wide, yet certification and training completion need accelerated block-level improvement."
        : "Teacher quality and staffing indicators are stable with selective deployment follow-up in low-index blocks.";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ed-content ed-teacher-view" data-testid="executive-teacher-tab">
      <section className="ed-hero-card ed-teacher-hero">
        <div className="ed-teacher-layout">
          <div className="ed-teacher-summary">
            <div
              className="ed-teacher-gauge"
              aria-label={`Teacher quality index ${qualityIndex}`}
              style={{
                background: `radial-gradient(circle at center, white 0 53%, transparent 54%), conic-gradient(var(--ed-purple) 0 ${qualityIndex}%, rgba(139,92,246,.11) ${qualityIndex}% 100%)`,
              }}
            >
              <div>
                <strong>{qualityIndex}</strong>
                <span>Quality</span>
              </div>
            </div>
            <div className="ed-health-copy">
              <h2>Teacher Overview</h2>
              <p>{heroNarrative}</p>
              <div className="ed-status-row">
                {qualityIndex < 50 && (
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant="red"
                    onClick={() => scrollTo("ed-teacher-quality")}
                    ariaLabel="Critical quality gap — view teacher quality diagnostics"
                  >
                    Critical Quality Gap
                  </HeroStatusChip>
                )}
                {zeroSchools > 0 && (
                  <HeroStatusChip
                    chipClass="ed-status-chip"
                    variant="amber"
                    onClick={onSchoolCountClick ? () => onSchoolCountClick(0) : undefined}
                    ariaLabel={`${zeroSchools} zero-teacher schools — view school list`}
                  >
                    {fmt(zeroSchools)} Zero-Teacher Schools
                  </HeroStatusChip>
                )}
                <HeroStatusChip chipClass="ed-status-chip">{fmt(totalTeachers)} Teachers</HeroStatusChip>
              </div>
            </div>
          </div>

          <div className="ed-teacher-kpi-grid" aria-label="Teacher and staffing KPI cards">
            <KpiCard label="Total Teachers" value={fmt(totalTeachers)} hint="Available workforce">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Zero Teacher Schools"
              value={fmt(zeroSchools)}
              hint="Immediate deployment risk"
              iconVariant="red"
              onClick={onSchoolCountClick ? () => onSchoolCountClick(0) : undefined}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </KpiCard>
            <KpiCard
              label="One Teacher Schools"
              value={fmt(oneSchools)}
              hint="Coverage vulnerability"
              iconVariant="orange"
              onClick={onSchoolCountClick ? () => onSchoolCountClick(1) : undefined}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </KpiCard>
            <KpiCard
              label="Two Teacher Schools"
              value={fmt(twoSchools)}
              hint="Low staffing strength"
              iconVariant="cyan"
              onClick={onSchoolCountClick ? () => onSchoolCountClick(2) : undefined}
            >
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              </svg>
            </KpiCard>
            <KpiCard label="CTET Qualified" value={pct(ctetPct)} hint="Lowest quality signal" iconVariant="orange">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 21h8" />
                <path d="M12 17v4" />
                <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              </svg>
            </KpiCard>
            <KpiCard label="Female Representation" value={pct(femalePct)} hint="Representation strength" iconVariant="pink">
              <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="8" r="5" />
                <path d="M12 13v8" />
                <path d="M9 18h6" />
              </svg>
            </KpiCard>
          </div>
        </div>
      </section>

      <div className="ed-section-title" id="ed-teacher-quality">
        <div>
          <h2>Teacher Quality and Staffing Diagnostics</h2>
          <p>
            Quality indicators and risk metrics are separated into readable cards, making it easier for district leaders to
            distinguish quality gaps from staffing gaps.
          </p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-teacher-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ed-teacher-diagnostic-grid">
        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Teacher Quality Breakdown</h3>
              <p>Core teacher quality indicators that influence the overall teacher-quality index.</p>
            </div>
            <span className="ed-pill">Quality index</span>
          </div>
          <div className="ed-teacher-gauge-grid">
            {qualityBreakdown.length ? (
              qualityBreakdown.map((item) => (
                <MetricDonut
                  key={item.metric}
                  value={item.value}
                  label={item.metric}
                  subtitle={DONUT_META[item.metric]?.subtitle || ""}
                  variant={DONUT_META[item.metric]?.variant || ""}
                />
              ))
            ) : (
              <>
                <MetricDonut value={ctetPct} label="CTET Qualified" subtitle="Highest improvement priority" />
                <MetricDonut value={nishthaPct} label="NISHTHA Completed" subtitle="Training gap remains high" />
                <MetricDonut value={aadhaarPct} label="Aadhaar Verified" subtitle="Strong verification base" variant="purple" />
                <MetricDonut value={femalePct} label="Female Representation" subtitle="Positive representation signal" variant="pink" />
              </>
            )}
          </div>
        </article>

        <article className="ed-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Risk Metrics</h3>
              <p>Operational risk indicators to prioritize deployment, retirement planning and training interventions.</p>
            </div>
            <span className="ed-pill">Staffing risk</span>
          </div>
          <div className="ed-teacher-risk-grid">
            <div className="ed-teacher-risk-card orange">
              <strong>{pct(risk.retirement_risk_pct)}</strong>
              <span>
                Retirement Risk · {fmt(risk.retirement_risk_count)} teachers
              </span>
            </div>
            <div className="ed-teacher-risk-card">
              <strong>{demographic.avg_service_years ?? 0}</strong>
              <span>Average Service Years</span>
            </div>
            <div className="ed-teacher-risk-card green">
              <strong>{pct(risk.growth_rate)}</strong>
              <span>Year-on-Year Growth</span>
            </div>
            <div className="ed-teacher-risk-card purple">
              <strong>{demographic.gender_parity_index ?? 0}</strong>
              <span>Gender Parity Ratio</span>
            </div>
          </div>
        </article>
      </section>

      {(profBars.length > 0 || acadBars.length > 0) && (
        <>
          <div className="ed-section-title">
            <div>
              <h2>Qualification Distribution</h2>
              <p>
                Large horizontal charts replace the compressed view so users can clearly compare professional and academic
                qualifications.
              </p>
            </div>
            <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-teacher-table")}>
              View block summary →
            </button>
          </div>

          <section className="ed-teacher-chart-grid">
            {profBars.length > 0 && (
              <article className="ed-panel">
                <div className="ed-panel-head">
                  <div>
                    <h3>Professional Qualification Distribution</h3>
                    <p>Share of teachers by professional qualification category.</p>
                  </div>
                  <span className="ed-pill">Professional</span>
                </div>
                <BarList items={profBars} barClassFn={profBarClass} />
              </article>
            )}
            {acadBars.length > 0 && (
              <article className="ed-panel">
                <div className="ed-panel-head">
                  <div>
                    <h3>Academic Qualification Distribution</h3>
                    <p>Academic qualification mix of teachers across the selected district.</p>
                  </div>
                  <span className="ed-pill">Academic</span>
                </div>
                <BarList items={acadBars} barClassFn={acadBarClass} />
              </article>
            )}
          </section>
        </>
      )}

      <div className="ed-section-title" id="ed-teacher-actions">
        <div>
          <h2>Insights and Next Actions</h2>
          <p>
            Teacher findings are converted into prioritised actions so the district can move from reporting to deployment
            and capacity building.
          </p>
        </div>
        <button type="button" className="ed-section-action" onClick={() => scrollTo("ed-teacher-table")}>
          View staffing table →
        </button>
      </div>

      <section className="ed-teacher-lower-grid">
        <article className="ed-teacher-insights-panel">
          <div className="ed-panel-head">
            <div>
              <h3>Teacher Workforce Signals</h3>
              <p>Summarized from teacher count, school staffing, training, qualification and retirement-risk indicators.</p>
            </div>
            <span className="ed-pill">Rule-based</span>
          </div>
          <div className="ed-teacher-insight-stack">
            <article className="ed-teacher-insight-card">
              <span className="ed-icon-box red">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
              </span>
              <div>
                <h4>{weakestQualitySignal.name} is the weakest quality signal</h4>
                <p>
                  Quality index is {qualityIndex}. Lowest indicator is {weakestQualitySignal.name} at {pct(weakestQualitySignal.value)}
                  {weakestQualitySignal.name === "CTET qualification" ? `; NISHTHA is at ${pct(nishthaPct)}` : ""}
                  {weakestQualitySignal.name === "NISHTHA completion" ? `; CTET is at ${pct(ctetPct)}` : ""}.
                </p>
              </div>
              <span className="ed-teacher-priority high">Critical</span>
            </article>
            <article className="ed-teacher-insight-card">
              <span className="ed-icon-box orange">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 21v-2a6 6 0 0 1 12 0v2" />
                </svg>
              </span>
              <div>
                <h4>Zero-teacher and one-teacher schools need deployment focus</h4>
                <p>
                  {fmt(zeroSchools)} zero-teacher schools and {fmt(oneSchools)} one-teacher schools should be reviewed
                  before the next staffing cycle.
                </p>
              </div>
              <span className="ed-teacher-priority high">High</span>
            </article>
            <article className="ed-teacher-insight-card">
              <span className="ed-icon-box purple">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 21h8" />
                  <path d="M12 17v4" />
                  <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
                </svg>
              </span>
              <div>
                <h4>Qualification base is strong but certification is weak</h4>
                <p>
                  Professional and academic qualifications are reasonable, but formal certification and training completion
                  need acceleration.
                </p>
              </div>
              <span className="ed-teacher-priority">Medium</span>
            </article>
            <article className="ed-teacher-insight-card">
              <span className="ed-icon-box green">
                <svg className="ed-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m3 17 6-6 4 4 8-8" />
                  <path d="M14 7h7v7" />
                </svg>
              </span>
              <div>
                <h4>Representation and verification are stable</h4>
                <p>
                  Female representation is {pct(femalePct)} and Aadhaar verification is {pct(aadhaarPct)}, providing a
                  strong base for workforce governance.
                </p>
              </div>
              <span className="ed-teacher-priority good">Stable</span>
            </article>
          </div>
        </article>

        <aside className="ed-teacher-action-panel">
          <h3>Recommended Action Queue</h3>
          <div className="ed-action-card">
            <article className="ed-action-item">
              <span className="ed-teacher-small-icon red">1</span>
              <div>
                <h4>Deploy teachers to zero-teacher schools</h4>
                <p>
                  Prioritize {fmt(zeroSchools)} zero-teacher schools and assign temporary cluster-level support immediately.
                </p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-teacher-small-icon orange">2</span>
              <div>
                <h4>Launch CTET/NISHTHA improvement sprint</h4>
                <p>Create monthly targets for teacher certification and training completion block-wise.</p>
              </div>
            </article>
            <article className="ed-action-item">
              <span className="ed-teacher-small-icon green">3</span>
              <div>
                <h4>Plan retirement-risk replacement</h4>
                <p>
                  Map {fmt(risk.retirement_risk_count)} retirement-risk teachers to subject, school and block replacement
                  plans.
                </p>
              </div>
            </article>
          </div>
          <button type="button" className="ed-teacher-learn-more" onClick={() => scrollTo("ed-teacher-table")}>
            <span>Open staffing action tracker</span>
            <span>→</span>
          </button>
        </aside>
      </section>

      <section className="ed-panel ed-table-card" id="ed-teacher-table">
        <div className="ed-table-head">
          <div>
            <h3>Block-Wise Staffing Summary</h3>
            <p>Detailed block table for teacher strength, low-staffing schools, quality indicators and action priority.</p>
          </div>
          <div className="ed-map-table-tools">
            <button
              type="button"
              className="ed-map-filter-chip"
              onClick={() => setPriorityOnly((v) => !v)}
            >
              {priorityOnly ? "Show all ▾" : "High priority only ▾"}
            </button>
          </div>
        </div>
        <table className="ed-table" aria-label="Block-wise teacher staffing summary">
          <thead>
            <tr>
              <th>Block</th>
              <th>Teachers</th>
              <th>Zero Teacher Schools</th>
              <th>One Teacher Schools</th>
              <th>Two Teacher Schools</th>
              <th>CTET Qualified</th>
              <th>NISHTHA Completed</th>
              <th>Quality Index</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length ? (
              tableRows.map((row) => (
                <tr key={row.block_code || row.block_name}>
                  <td>
                    {row.block_code ? (
                      <BlockLink blockCode={row.block_code}>{row.block_name}</BlockLink>
                    ) : (
                      row.block_name
                    )}
                  </td>
                  <td>{fmt(row.teachers)}</td>
                  <td>{fmt(row.zero_teacher_schools ?? 0)}</td>
                  <td>{fmt(row.one_teacher_schools ?? 0)}</td>
                  <td>{fmt(row.two_teacher_schools ?? 0)}</td>
                  <td>{pct(row.ctet_pct)}</td>
                  <td>{pct(row.nishtha_pct)}</td>
                  <td className="ed-score">{row.quality_index}</td>
                  <td>
                    <span className={`ed-tag ${row.priority.className}`}>{row.priority.label}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9}>No block staffing data available for {scopeLabel}.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default ExecutiveTeacherStaffingTab;
